/**
 * @module Logger
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any*/
import { Injectable } from '@angular/core';
import { RebLogger, LogMessage } from '@core/reb-logger';
import { WorkerService } from '@core/worker/services/worker.service';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { SETTINGS } from '@environment/settings';
import { AppConfig } from '../../../root/app.config';
import { Log, LogInfo } from '../models/log.interface';
import { WorkerRequestArgs } from '@core/worker/models/worker-request-args';
import { CommonHeadersBuilder } from '@core/api/utils/common-headers-builder';
import { HeadersBuilderFactory } from '@core/api/utils/headers-builder-factory';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { LOGGER_CONSTANTS } from '../logger.constants';
import { WindowRefService } from '@support/shared/services/window-ref.service';
import { CHANNEL_CONST } from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { HttpHeaders } from '@angular/common/http';

/**
 * Service used for logger helper function
 */
@Injectable()
export class LoggerService extends RebLogger {
  public readonly windowRef: any; // Need to add dynamic global function
  constructor(
    private workerService: WorkerService,
    private commonUtil: CommonUtilService,
    private stateStoreUtil: StateStoreUtilsService,
    private appConfig: AppConfig,
    private windowRefService: WindowRefService
  ) {
    super(
      {
        level: 'VERBOSE', // 'ALL','DEFAULT', 'VERBOSE', 'OFF'
        batchSize: 40,
        storeBufferLevel: 'VERBOSE', // 'ALL','DEFAULT', 'VERBOSE', 'OFF'
        sendBufferLevel: 'OFF', // 'ALL','DEFAULT', 'VERBOSE', 'OFF'
        bufferSize: 40,
        endPoint: `${SETTINGS.MS_ENDPOINT}`.concat(API_MAP.API_522_log.toString()),
        output: 'SERVER', // 'SERVER', 'CONSOLE', 'BOTH'
      },
      'UI'
    );

    this.windowRef = this.windowRefService.nativeWindow();

    /* To change log level from console, provide 'ALL','DEFAULT', 'VERBOSE' or 'OFF' in parameter */
    this.windowRef.setConsoleLogOption = (consoleLogLevel: string) => {
      this.setLoggerOption({ consoleLevel: consoleLogLevel });
    };

    // update EndPoint from config
    const DOMAIN = this.appConfig.getConfig('esGatewayEndpoint') || SETTINGS.MS_ENDPOINT;
    this.setLoggerOption({ endPoint: `${DOMAIN}`.concat(API_MAP.API_522_log.url.toString()) });
    // TODO Commenting as part of PR73040
    /* this.stateStoreService.addListener('app.token', (path: string, value: string) => {
      this.stateStoreService.removeListener('app.token');
      // update logger setting from API
      const headers: Headers = new Headers();
      headers.append('layerid', 'ui');
      const authorizationToken =  SETTINGS.CHANNEL.toLowerCase() === CHANNEL_CONST.web ? 'Bearer ' + value : value;
      headers.append('Authorization', authorizationToken);
      this.apiGatewayService.getRequest(API_MAP.API_505_getLoggingLevel, headers,
        null, null, null, null, false).subscribe((res: HttpResponse<Object>) => {
        if (res) {
          this.setLoggerOption(res.body);
        }
      }, () => {
          this.setLoggerOption({
            isEnable: false
          });
      });
    } );
	*/
  }
  /**
   * trace method : similiar to console.trace
   */
  public trace(
    moduleName: string,
    componentName: string,
    errorDesc: string,
    errorCode: string = 'NA',
    errorStack: any = {}
  ): void {
    this.rebTrace(moduleName, componentName, errorDesc, this.getLogInfo(), errorCode, errorStack);
  }

  /**
   * info method : similiar to console.info
   */
  public info(
    moduleName: string,
    componentName: string,
    errorDesc: string,
    errorCode: string = 'NA',
    errorStack: any = {}
  ): void {
    this.rebInfo(moduleName, componentName, errorDesc, this.getLogInfo(), errorCode, errorStack);
  }

  /**
   * debug method : similiar to console.debug
   */

  public debug(
    moduleName: string,
    componentName: string,
    errorDesc: string,
    errorCode: string = 'NA',
    errorStack: any = {}
  ): void {
    this.rebDebug(moduleName, componentName, errorDesc, this.getLogInfo(), errorCode, errorStack);
  }

  /**
   * warning method : similiar to console.warn
   */
  public warning(
    moduleName: string,
    componentName: string,
    errorDesc: string,
    errorCode: string = 'NA',
    errorStack: any = {}
  ): void {
    this.rebWarning(moduleName, componentName, errorDesc, this.getLogInfo(), errorCode, errorStack);
  }

  /**
   * error method : similiar to console.error
   */
  public error(
    moduleName: string,
    componentName: string,
    errorDesc: string,
    errorCode: string = 'NA',
    errorStack: any = {}
  ): void {
    this.rebError(moduleName, componentName, errorDesc, this.getLogInfo(), errorCode, errorStack);
  }

  /**
   * fatal method : similiar to console.error but with highest property.
   * Send to server even log level is 'OFF'
   */
  public fatal(
    moduleName: string,
    componentName: string,
    errorDesc: string,
    errorCode: string = 'NA',
    errorStack: any = {}
  ): void {
    this.rebFatal(moduleName, componentName, errorDesc, this.getLogInfo(), errorCode, errorStack);
  }

  /**
   * sendBatch :  Extending from RebLogger Send log data to webworker provider
   * so that it can communicate to reb-webwroker by postmessage
   * @param logBatch
   * @param endPoint
   */
  public sendBatch(logBatch: LogMessage[], endPoint: string): void {
    const logObject: Log = {
      lg: logBatch,
    };
    // sending log to server for web worker
    const workerRequest: WorkerRequestArgs = {
      headers: this.getCommonHeader(),
    };
    // TODO Commenting as part of PR73040
    if (SETTINGS.CHANNEL.toLowerCase() !== CHANNEL_CONST.web) {
      this.workerService.post(endPoint, logObject, workerRequest).subscribe();
    } else if (endPoint.indexOf('/v1/las') === -1) {
      this.workerService.post(endPoint, logObject, workerRequest).subscribe();
    }
  }

  /**
   * getLogInfo :  get common Headers info and send to logs
   */
  private getLogInfo(): object {
    const headers: HttpHeaders = this.getCommonHeader();
    const logInfo: LogInfo = {
      event_type: LOGGER_CONSTANTS.EVENT_TYPE,
      event_sub_type: LOGGER_CONSTANTS.EVENT_SUB_TYPE,
      payload_type: LOGGER_CONSTANTS.PAYLOAD_TYPE,
      interaction_id: headers.get('interactionId'),
      workflow_id: headers.get('workflowId'),
      transaction_id: headers.get('transactionId'),
      screen_id: headers.get('screenId'),
      activity_id: headers.get('activityId'),
      cart_id: headers.get('cartId'),
      sender_id: headers.get('senderId'),
      application_id: headers.get('applicationId'),
      store_id: headers.get('storeId'),
      channel_id: headers.get('channelId'),
      session_id: headers.get('sessionId'),
      application_user_id: headers.get('applicationUserId'),
    };
    return logInfo;
  }

  /**
   * getCommonHeader :  get common Headers info and return
   */
  private getCommonHeader(): HttpHeaders {
    const headers: HttpHeaders = new HttpHeaders();
    const chb: CommonHeadersBuilder = new HeadersBuilderFactory(
      this.stateStoreUtil,
      this.commonUtil
    ).getHeaderbuilder(headers, SETTINGS.CHANNEL.toLocaleLowerCase());
    const requestHeaders: HttpHeaders = chb.getHeaders();
    return requestHeaders;
  }
}
